import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  buildFileTitle,
  containsForbiddenFileNameChars,
  FILE_TITLE_MAX_LENGTH,
  FORBIDDEN_FILE_NAME_CHARS,
  getFileBaseName,
  getNamedFileExtension,
  NamedFile,
} from '../../utils/file-name.utils';

const forbiddenCharsValidator = (control: AbstractControl<string>): ValidationErrors | null =>
  containsForbiddenFileNameChars(control.value || '') ? { forbiddenChars: true } : null;

const notBlankValidator = (control: AbstractControl<string>): ValidationErrors | null =>
  (control.value || '').trim().length === 0 ? { required: true } : null;

/**
 * Small dialog for renaming a file. Only the base name is editable, the extension of the stored file is kept.
 * Closes with the new full title (base name + extension), dismissed on Esc / Cancel.
 */
@Component({
  selector: 'isophi-rename-file-modal',
  templateUrl: './rename-file-modal.component.html',
  styleUrls: ['./rename-file-modal.component.scss'],
  standalone: false,
})
export class RenameFileModalComponent implements OnInit {
  namedFile: NamedFile;

  extension = '';
  maxLength = FILE_TITLE_MAX_LENGTH;
  forbiddenChars = FORBIDDEN_FILE_NAME_CHARS.join(' ');

  control = new FormControl<string>('', { nonNullable: true });
  form = new FormGroup({ name: this.control });

  readonly activeModal = inject(NgbActiveModal);

  static open(modalService: NgbModal, namedFile: NamedFile): Promise<string | null> {
    const modalRef = modalService.open(RenameFileModalComponent, { centered: true });
    modalRef.componentInstance.namedFile = namedFile;
    return modalRef.result.then(
      (title: string) => title,
      () => null,
    );
  }

  ngOnInit(): void {
    this.extension = getNamedFileExtension(this.namedFile);
    this.maxLength = FILE_TITLE_MAX_LENGTH - this.extension.length;
    this.control.setValidators([notBlankValidator, Validators.maxLength(this.maxLength), forbiddenCharsValidator]);
    this.control.setValue(getFileBaseName(this.namedFile));
  }

  submit(): void {
    this.control.markAsTouched();
    if (this.control.invalid) {
      return;
    }
    this.activeModal.close(buildFileTitle(this.control.value, this.extension));
  }
}
